import React, { useCallback, useMemo, useState } from 'react';
import { Button, Dropdown, Form, Select, Tooltip, token } from '@bynder/design-system';
import { IconQuestion } from '@bynder/icons';
import { useTranslate } from '@bynder/localization';
import useFonts from 'packages/pages/editor/RightSideMenu/Shots/Text/Fonts/useFonts';
import useTextSelection from 'packages/hooks/useTextSelection';
import generateTestId from 'packages/helpers/testIdHelpers';
import type { Props } from './types';
import type { FontFamily as FontFamilyType } from '../types';

const FontFamily = ({ creativeModel, selectedElement, disabled }: Props) => {
    const { id, locked } = selectedElement;
    const { fontIdData, fontId: rawFontIds } = useTextSelection();
    const [, , mixedValue] = fontIdData;
    const [searchValue, setSearchValue] = useState('');
    const { loadDefaultFontByFamily, fonts, fontsList, currentFont } = useFonts();
    const { translate } = useTranslate();

    const isSelected = useCallback(
        (family: FontFamilyType) =>
            family.fonts.some((f) => f.referenceIds.some((referenceId) => rawFontIds.includes(String(referenceId)))),
        [rawFontIds],
    );

    const isFontActive = currentFont?.isActive;

    const [selectedFamilyNames, familyName] = useMemo(() => {
        const selected = Object.values(fontsList).filter(isSelected);
        const names = new Set(selected.map((family) => family.name));
        const matched = selected[0] ?? fonts.find(isSelected);

        return [names, rawFontIds.length > 1 ? mixedValue : (matched?.name ?? 'Default')];
    }, [rawFontIds, fonts, fontsList, isSelected, mixedValue]);

    const isFontMissing = useMemo(() => {
        const known = fonts.flatMap((family) => family.fonts.flatMap((f) => f.referenceIds.map(String)));

        return rawFontIds.some((id) => !known.includes(id));
    }, [rawFontIds, fonts]);

    const familyListArray = useMemo(() => {
        if (searchValue === '') {
            return Object.values(fontsList);
        }

        return Object.values(fontsList).filter((f) => f.name.toLowerCase().includes(searchValue.toLowerCase()));
    }, [fontsList, searchValue]);

    const handleChange = useCallback<(family: FontFamilyType) => Promise<void>>(
        async (family) => {
            const font = loadDefaultFontByFamily(family.fonts);
            const param = {
                updateTextSettingForSelection: {
                    settings: {
                        fontId: font.id.toString(),
                    },
                },
            };

            creativeModel.updateElement(id, param);
        },
        [creativeModel, id, loadDefaultFontByFamily],
    );

    return (
        <Form.Group>
            <Dropdown
                minWidth="288px"
                onClose={() => setSearchValue('')}
                {...generateTestId('shots_text_typeface')}
                trigger={({ isOpen, ...triggerProps }) => (
                    <Button
                        aria-label="shots_text_typeface"
                        isPressed={isOpen}
                        icon={
                            !isFontActive ? (
                                <Tooltip content={translate('editor.sidebar.shots.fonts.deactivated.tooltip')}>
                                    {/* TODO: Replace to "missingFontIcon" when it is available */}
                                    <IconQuestion style={{ color: token.orange500 }} />
                                </Tooltip>
                            ) : undefined
                        }
                        rightIcon={<Dropdown.Arrow />}
                        isDisabled={disabled || locked}
                        isFullWidth
                        {...triggerProps}
                    >
                        <Dropdown.SelectButtonText>{familyName}</Dropdown.SelectButtonText>
                    </Button>
                )}
            >
                <Select.SearchInput
                    value={searchValue}
                    onChange={setSearchValue}
                    placeholder={translate('editor.sidebar.shots.fonts.fontfamily.search')}
                    aria-label={translate('editor.sidebar.shots.fonts.fontfamily.search.label')}
                />
                {familyListArray.map((family) => (
                    <Dropdown.Item
                        key={family.name}
                        onClick={() => handleChange(family)}
                        isChecked={selectedFamilyNames.has(family.name)}
                        isDisabled={selectedFamilyNames.has(family.name) && !isFontActive}
                    >
                        {family.name}
                    </Dropdown.Item>
                ))}
            </Dropdown>
            {isFontMissing && (
                <Form.HelperText icon={<Form.IconError />}>
                    {translate('editor.sidebar.shots.fonts.fontfamily.missing')}
                </Form.HelperText>
            )}
        </Form.Group>
    );
};

export default FontFamily;
